/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
'use client';

import { Camera, Download, Eye, Grid3X3, Heart, Images, MessageCircle, Share2 } from 'lucide-react';
import React, { useMemo } from 'react';

import { appSettings } from '@/settings/app-settings';

import { useOpenMessage } from '../../hooks/useOpenMessage';
import { ContentRegistry } from '../registries';
import { ContentItemProps } from '../registries/ContentRegistry';

const ImageCard: React.FC<ContentItemProps> = ({ content, variant = 'card', onAction, className }) => {
  const isMinimal = variant === 'minimal';
  const isExpanded = variant === 'expanded';
  const { openMessage } = useOpenMessage();

  // Check if this is a grid/masonry card with multiple images
  // Handle both 'image' and 'images' properties from the API
  const images =
    content.data?.image?.map((img: any) => {
      // Safely extract tags from traditional_search_description
      let tags: string[] = [];
      if (img.content.summary?.traditional_search_description) {
        tags = img.content.summary.traditional_search_description
          .split(',')
          .map((tag: string) => tag.trim())
          .filter((tag: string) => tag.length > 0);
      }

      // Extract questions from AI response
      const aiQuestions = img.content.summary?.questions || [];

      return {
        id: img._id,
        src: img.content?.url,
        alt: img.content.summary?.machine_readable_description || '',
        caption: img.content.summary?.semantic_search_description || '',
        metadata: {
          customTags: tags.slice(0, 5), // Use first 5 tags from AI
          customQuestions: aiQuestions, // Store AI questions
        },
      };
    }) || [];

  const isImageGrid =
    content.data?.cardType === 'imagegrid' ||
    content.data?.cardType === 'image' ||
    (Array.isArray(images) && images.length > 1);
  const displayImages = Array.isArray(images) ? images.slice(0, 6) : []; // Max 6 images
  const remainingCount = Array.isArray(images) ? Math.max(0, images.length - displayImages.length) : 0;

  const handleClick = () => {
    onAction?.('inspect', content);
  };

  const handleImageClick = (e: React.MouseEvent, imageIndex?: number) => {
    e.stopPropagation();
    if (imageIndex !== undefined) {
      // Transform images to standard format
      const standardImages = images.map((img: any) => ({
        id: img.id || `img-${Math.random().toString(36).substr(2, 9)}`,
        src: img.src || img.url || img,
        alt: img.alt || '',
        caption: img.caption || img.content?.summary?.semantic_search_description || '',
        metadata: {
          customTags: img.metadata?.customTags || img.content?.metadata?.customTags || [],
          customQuestions: img.metadata?.customQuestions || img.content?.metadata?.customQuestions || [],
        },
      }));

      // Pass the specific image index to open that image in the inspect view
      onAction?.('inspect', {
        ...content,
        data: {
          selectedImageIndex: imageIndex,
          images: standardImages,
          image: standardImages, // Override the old format
          viewMode: 'single',
          cardType: content.data?.cardType,
          source: content.data?.source,
        },
      });
    } else {
      onAction?.('inspect', content);
    }
  };

  const handleViewAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Transform images to standard format
    const standardImages = images.map((img: any) => ({
      id: img.id || `img-${Math.random().toString(36).substr(2, 9)}`,
      src: img.src || img.url || img,
      alt: img.alt || '',
      caption: img.caption || img.content?.summary?.semantic_search_description || '',
      metadata: {
        customTags: img.metadata?.customTags || img.content?.metadata?.customTags || [],
        customQuestions: img.metadata?.customQuestions || img.content?.metadata?.customQuestions || [],
      },
    }));

    onAction?.('inspect-grid', {
      ...content,
      data: {
        images: standardImages,
        image: standardImages, // Override the old format
        viewMode: 'grid',
        cardType: content.data?.cardType,
        source: content.data?.source,
      },
    });
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const imageUrl = content.media?.hero?.url || content.media?.thumbnail?.url;
    if (!imageUrl) return;

    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = content.title || 'image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Notify parent that download was triggered
      onAction?.('download', content);
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  };

  const handleChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    const vendorId = content.data?.vendorId || content.source?.author?.id;
    const vendorName = content.source?.author?.name;
    openMessage({
      vendorId,
      vendorName,
      contentContext: content,
    });
  };

  // Helper function to get image URL
  const getImageUrl = (image: any) => {
    // Handle the new mapped structure
    if (image.src) {
      const url = image.src;
      // If it's a relative URL or filename, prepend the S3 URL
      if (url && !url.startsWith('http') && !url.startsWith('data:')) {
        return `${appSettings.api.S3_MEDIA_COMPRESSED}${url}`;
      }
      return url;
    }

    // Fallback for old structure
    let url = '';
    if (typeof image === 'string') {
      url = image;
    } else if (image.url) {
      url = image.url;
    } else if (image.content?.url) {
      url = image.content.url;
    }

    // If it's a relative URL or filename, prepend the S3 URL
    if (url && !url.startsWith('http') && !url.startsWith('data:')) {
      return `${appSettings.api.S3_MEDIA_COMPRESSED}${url}`;
    }

    return url;
  };

  // Helper function to get image description
  const getImageDescription = (image: any, index: number) => {
    // Handle the new mapped structure
    if (image.caption) {
      return image.caption;
    }
    if (image.alt) {
      return image.alt;
    }

    // Fallback for old structure
    if (image.content?.summary?.human_readable_summary) return image.content.summary.human_readable_summary;
    if (image.content?.summary?.semantic_search_description)
      return image.content.summary.semantic_search_description;
    return `Image ${index + 1}`;
  };

  // Masonry layout calculation for image grid
  const masonryLayout = useMemo(() => {
    if (!isImageGrid || displayImages.length === 0) return null;

    // Define layouts based on image count
    const layouts: { [key: number]: string[] } = {
      1: ['col-span-2 row-span-2'],
      2: ['col-span-1 row-span-2', 'col-span-1 row-span-2'],
      3: ['col-span-2 row-span-2', 'col-span-1 row-span-1', 'col-span-1 row-span-1'],
      4: ['col-span-1 row-span-1', 'col-span-1 row-span-1', 'col-span-1 row-span-1', 'col-span-1 row-span-1'],
      5: [
        'col-span-1 row-span-2',
        'col-span-1 row-span-1',
        'col-span-1 row-span-1',
        'col-span-1 row-span-1',
        'col-span-1 row-span-1',
      ],
      6: [
        'col-span-1 row-span-1',
        'col-span-1 row-span-1',
        'col-span-1 row-span-1',
        'col-span-1 row-span-1',
        'col-span-1 row-span-1',
        'col-span-1 row-span-1',
      ],
    };

    return layouts[displayImages.length] || layouts[6];
  }, [displayImages.length, isImageGrid]);

  if (isMinimal) {
    return (
      <div
        className={`inspect-theme-bg-elevated rounded-lg overflow-hidden cursor-pointer hover:inspect-theme-shadow transition-all ${className}`}
        onClick={handleClick}
      >
        {isImageGrid && displayImages.length > 0 ? (
          <div className="relative">
            <div className="grid grid-cols-2 gap-0.5 p-2">
              {displayImages.slice(0, 4).map((image, index) => (
                <div key={index} className="relative aspect-square overflow-hidden rounded">
                  <img
                    src={getImageUrl(image)}
                    alt={getImageDescription(image, index)}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            <div className="absolute bottom-2 left-2 right-2 bg-black/70 backdrop-blur-sm rounded px-2 py-1">
              <h4 className="text-white font-medium text-sm line-clamp-1 flex items-center gap-1">
                <Images className="w-3 h-3" />
                {content.title || `${Array.isArray(images) ? images.length : 0} images`}
              </h4>
            </div>
          </div>
        ) : content.media?.thumbnail ? (
          <div className="relative aspect-video">
            <img
              src={content.media.thumbnail.url}
              alt={content.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity">
              <div className="absolute bottom-2 left-2 right-2">
                <h4 className="text-white font-medium text-sm line-clamp-1">{content.title}</h4>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <article
      className={`inspect-theme-bg-elevated rounded-xl overflow-hidden ${
        isImageGrid ? '' : 'cursor-pointer hover:inspect-theme-shadow-lg'
      } transition-all duration-300 ${className}`}
      onClick={!isImageGrid ? handleClick : undefined}
    >
      {/* Image Grid/Masonry Layout */}
      {isImageGrid && displayImages.length > 0 ? (
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Images className="w-5 h-5 text-purple-500" />
              <h3 className="font-semibold text-lg inspect-theme-text">
                {content.title || 'Image Collection'}
              </h3>
              <span className="text-sm inspect-theme-text-muted">
                ({Array.isArray(images) ? images.length : 0} images)
              </span>
            </div>
            {remainingCount > 0 && (
              <button
                onClick={handleViewAll}
                className="text-sm font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
              >
                +{remainingCount} more
              </button>
            )}
          </div>

          {/* Masonry Grid */}
          <div className="grid grid-cols-2 gap-2 auto-rows-[100px]">
            {displayImages.map((image, index) => {
              const layoutClass = masonryLayout?.[index] || 'col-span-1 row-span-1';
              return (
                <div
                  key={index}
                  className={`${layoutClass} relative group overflow-hidden rounded-lg cursor-pointer`}
                  onClick={(e) => handleImageClick(e, index)}
                >
                  <img
                    src={getImageUrl(image)}
                    alt={getImageDescription(image, index)}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-2 left-2 right-2">
                      <p className="text-white text-xs line-clamp-2">
                        {image.caption || `Image ${index + 1}`}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* View All Button */}
          {Array.isArray(images) && images.length > 6 && (
            <button
              onClick={handleViewAll}
              className="w-full mt-4 py-3 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg font-medium hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors flex items-center justify-center gap-2"
            >
              <Grid3X3 className="w-5 h-5" />
              View all {images.length} images
            </button>
          )}
        </div>
      ) : (
        /* Single Image Display */
        <>
          <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
            {content.media?.thumbnail && (
              <img
                src={content.media.thumbnail.url}
                alt={content.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            )}

            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300">
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <h3 className="font-semibold text-lg mb-1">{content.title}</h3>
                {content.source?.author && (
                  <p className="text-sm opacity-90">by {content.source.author.name}</p>
                )}
              </div>
            </div>

            {/* Image metadata badge */}
            {content.data?.format && (
              <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium uppercase">
                {content.data.format}
              </div>
            )}
          </div>
        </>
      )}

      {/* Content - Only show for single images */}
      {!isImageGrid && (
        <div className={`p-5 ${isExpanded ? 'space-y-4' : 'space-y-3'}`}>
          {/* Title & Author */}
          <div>
            <h3
              className={`font-semibold inspect-theme-text ${
                isExpanded ? 'text-xl' : 'text-lg'
              } line-clamp-2`}
            >
              {content.title}
            </h3>
            {content.source?.author && (
              <p className="text-sm inspect-theme-text-muted mt-1">
                <Camera className="w-3 h-3 inline mr-1" />
                {content.source.author.name}
              </p>
            )}
          </div>

          {/* Description */}
          {content.description && (
            <p className={`inspect-theme-text-muted ${isExpanded ? 'line-clamp-3' : 'line-clamp-2'} text-sm`}>
              {content.description}
            </p>
          )}

          {/* Image Details */}
          {isExpanded && content.data && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {content.data.dimensions && (
                <div>
                  <span className="inspect-theme-text-muted">Dimensions:</span>
                  <span className="inspect-theme-text ml-2">
                    {content.data.dimensions.width} × {content.data.dimensions.height}
                  </span>
                </div>
              )}
              {content.data.fileSize && (
                <div>
                  <span className="inspect-theme-text-muted">Size:</span>
                  <span className="inspect-theme-text ml-2">
                    {(content.data.fileSize / 1024 / 1024).toFixed(1)} MB
                  </span>
                </div>
              )}
              {content.data.camera && (
                <div>
                  <span className="inspect-theme-text-muted">Camera:</span>
                  <span className="inspect-theme-text ml-2">{content.data.camera}</span>
                </div>
              )}
              {content.data.location && (
                <div>
                  <span className="inspect-theme-text-muted">Location:</span>
                  <span className="inspect-theme-text ml-2">{content.data.location}</span>
                </div>
              )}
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {content.tags?.slice(0, 4).map((tag, index) => (
              <span key={index} className="inspect-theme-tag text-xs px-2 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>

          {/* Engagement Stats */}
          <div className="flex items-center gap-4 text-sm inspect-theme-text-muted pt-2 border-t inspect-theme-border">
            {content.engagement?.views !== undefined && (
              <span className="flex items-center gap-1.5">
                <Eye className="w-4 h-4" />
                {content.engagement.views.toLocaleString()}
              </span>
            )}
            {content.engagement?.likes !== undefined && (
              <span className="flex items-center gap-1.5">
                <Heart className="w-4 h-4" />
                {content.engagement.likes.toLocaleString()}
              </span>
            )}
            {content.engagement?.shares !== undefined && (
              <span className="flex items-center gap-1.5">
                <Share2 className="w-4 h-4" />
                {content.engagement.shares}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleDownload}
              className="flex-1 inspect-theme-button-primary px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="w-4 h-4 inline mr-2" />
              Download
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAction?.('share', content);
              }}
              className="p-2 inspect-theme-button-secondary rounded-lg transition-colors"
              title="Share"
            >
              <Share2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleChat}
              className="p-2 inspect-theme-button-secondary rounded-lg transition-colors"
              title="Chat about this image"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

// Auto-register with ContentRegistry
ContentRegistry.register('image', ImageCard);

export default ImageCard;
